import { useState } from "react";
import { useBilling } from "@/context/BillingContext";
import EnableNotifications from "@/components/EnableNotifications";
import PlanBadge from "@/components/trips/PlanBadge";
import NewTripDialog, { NewTripTrigger } from "@/components/trips/NewTripDialog";
import TripCard from "@/components/trips/TripCard";
import EmptyTripsState from "@/components/trips/EmptyTripsState";
import { useTrips } from "@/hooks/useTrips";

function TripsGridSkeleton() {
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
      {[0, 1, 2].map((i) => <div key={i} className="h-64 rounded-2xl bg-muted animate-pulse" />)}
    </div>
  );
}

function TripsGrid({ trips, onDelete }) {
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
      {trips.map((t, idx) => (
        <TripCard key={t.id} trip={t} index={idx} onDelete={onDelete} />
      ))}
    </div>
  );
}

function TripsHeader({ plan, onOpen }) {
  return (
    <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
      <div>
        <div className="text-xs uppercase tracking-widest text-stone-500">Your vault</div>
        <h1 className="font-display text-4xl sm:text-5xl font-light tracking-tighter text-stone-900 mt-1">Trips</h1>
        <p className="text-stone-600 mt-2 max-w-xl">
          Every journey — for you, your family or your crew — kept together with people, tickets and receipts.
        </p>
        <PlanBadge plan={plan} />
      </div>
      <NewTripDialog
        trigger={NewTripTrigger}
        open={onOpen.value}
        onOpenChange={onOpen.set}
        onCreated={onOpen.onCreated}
      />
    </div>
  );
}

export default function Dashboard() {
  const [open, setOpen] = useState(false);
  const { plan } = useBilling();
  const { trips, loading, addTrip, removeTrip } = useTrips();

  return (
    <div className="space-y-8">
      <TripsHeader
        plan={plan}
        onOpen={{ value: open, set: setOpen, onCreated: addTrip }}
      />

      <EnableNotifications />

      {loading
        ? <TripsGridSkeleton />
        : trips.length === 0
          ? <EmptyTripsState onNew={() => setOpen(true)} />
          : <TripsGrid trips={trips} onDelete={removeTrip} />
      }
    </div>
  );
}
